/**
 * RequireAuth — route-level auth guard (D-18).
 *
 * Wraps any authenticated subtree. If `useAuth().isAuthenticated` is false,
 * redirects to /login preserving the intended destination via
 * `state.from = pathname + search`. LoginPage reads this back so a successful
 * sign-in returns the user to the page they tried to visit.
 *
 * Pairs with the global 401 bridge in App.tsx — apiFetch 401 triggers
 * `clearAuth()` + `navigate('/login')`, which causes this guard to render
 * <Navigate to="/login" /> on the next render cycle.
 */

import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '@/context/AuthContext';

export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }

  return <>{children}</>;
}
